import React, {useState} from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell, TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {useTheme} from '@mui/styles';



interface WarningEntry {
  id: string;
  tramId: number;
  message: string;
  warningType: string;
  timestamp: string;
  expectedTags: string[];
  missingTags: string[];
  extraTags: string[];
}

interface DetectorGroup {
  key: string;
  count: number;
  entries: WarningEntry[];
}

interface TramGroup {
  key: number;
  count: number;
  entries: WarningEntry[];
}

const extractDetector = (message: string) => {
  const match = message.match(/detector ([\w/-]+)/);
  return match ? match[1] : 'Unknown';
};


const WarningDashboard = ({ data }: { data: WarningEntry[] }) => {
  const theme = useTheme(); // Access the theme
  const [expanded, setExpanded] = useState<string | number | null>(null);
  const [tramChartView, setTramChartView] = useState<'chart' | 'table'>('chart');
  const [detectorChartView, setDetectorChartView] = useState<'chart' | 'table'>('chart');

  const toggleExpanded = (key: string | number) =>
    setExpanded((prev) => (prev === key ? null : key));

  const detectorData = data[0].detectorData;
  const tramData = data[0].tramData;


  return (

    <Box sx={{height: '100vh', overflowY: 'auto', width: '100%'}} p={3}>
      <Typography sx={{ color: theme.palette.text.primary}} variant="h5" gutterBottom>
        {' '}
        Tram Detector Warnings Dashboard
      </Typography>

      {/* DETECTOR SECTION */}
      <Box mt={4}>
        <Grid container justifyContent="space-between">
          <Typography sx={{ color: theme.palette.text.primary}} variant="h6">Top 10 Worst Detectors</Typography>
          <Button
            onClick={() =>
              setDetectorChartView((prev) => (prev === 'chart' ? 'table' : 'chart'))
            }
          >
            {detectorChartView === 'chart' ? 'Show Table' : 'Show Chart'}
          </Button>
        </Grid>

        {detectorChartView === 'chart' ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Object.entries(detectorData).map(([key, obj]: any) => ({name: key, value: obj.total_count}))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#1976d2" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <TableContainer sx={{ height: '100vh'  }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Detector</TableCell>
                  <TableCell>Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(detectorData).map(([key, obj]: any) => (
                  <React.Fragment key={key}>
                    <TableRow>
                      <TableCell>
                        <IconButton onClick={() => toggleExpanded(key)}>
                          <ExpandMoreIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>{key}</TableCell>
                      <TableCell>{obj.total_count}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} style={{padding: 0}}>
                        <Collapse in={expanded === key}>
                          <Box height={200} px={2}>
                            <ResponsiveContainer>
                              <BarChart
                                data={Object.entries(
                                  Object.entries(obj.trams).reduce(
                                    (acc, e) => {
                                      const id = e[0].toString().replace(/^10/, '');
                                      acc[id] = e[1];
                                      return acc;
                                    },
                                    {} as Record<string, number>,
                                  ),
                                ).map(([id, count]) => ({id, count}))}
                              >
                                <XAxis dataKey="id" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#8884d8" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* TRAM SECTION */}
      <Box mt={6}>
        <Grid container justifyContent="space-between">
          <Typography sx={{ color: theme.palette.text.primary}} variant="h6">Top 10 Trams with Most Warnings</Typography>
          <Button onClick={() => setTramChartView(prev => prev === 'chart' ? 'table' : 'chart')}>
            {tramChartView === 'chart' ? 'Show Table' : 'Show Chart'}
          </Button>
        </Grid>

        {tramChartView === 'chart' ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Object.entries(tramData).map(([key, obj]: any) => ({
              name: key.toString().replace(/^10/, ''),
              value: obj.total_count
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#ff9800" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Paper>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Tram ID</TableCell>
                  <TableCell>Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(tramData).map(([key, obj]: any) => (
                  <React.Fragment key={key}>
                    <TableRow>
                      <TableCell>
                        <IconButton onClick={() => toggleExpanded(key)}>
                          <ExpandMoreIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>{key.toString().replace(/^10/, '')}</TableCell>
                      <TableCell>{obj.total_count}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} style={{ padding: 0 }}>
                        <Collapse in={expanded === key}>
                          <Box height={200} px={2}>
                            <ResponsiveContainer>
                              <BarChart data={Object.entries(
                                Object.entries(obj.detectors).reduce((acc, e) => {
                                  const det = e[0];
                                  // const det = extractDetector(e.message);
                                  acc[det] = e[1];
                                  return acc;
                                }, {} as Record<string, number>)
                              ).map(([id, count]) => ({ id, count }))}>
                                <XAxis dataKey="id" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#ffa726" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {/* HEATMAP */}
      {/*    <Box mt={6}>*/}
      {/*  <Typography variant="h6" gutterBottom>*/}
      {/*    Heatmap: Detector Warnings Across Top 10 Trams*/}
      {/*  </Typography>*/}
      {/*  <Box sx={{ overflowX: 'auto' }}>*/}
      {/*    <Table size="small" stickyHeader>*/}
      {/*      <TableHead>*/}
      {/*        <TableRow>*/}
      {/*          <TableCell>Detector</TableCell>*/}
      {/*          {Object.keys(heatmapJson[0] || {})*/}
      {/*            .filter((key) => key !== 'detector')*/}
      {/*            .map((tramId) => (*/}
      {/*              <TableCell key={tramId} align="center">*/}
      {/*                {tramId.toString().replace(/^10/, '')}*/}
      {/*              </TableCell>*/}
      {/*            ))}*/}
      {/*        </TableRow>*/}
      {/*      </TableHead>*/}
      {/*      <TableBody>*/}
      {/*        {heatmapJson.map((row) => (*/}
      {/*          <TableRow key={row.detector}>*/}
      {/*            <TableCell>{row.detector}</TableCell>*/}
      {/*            {Object.keys(row)*/}
      {/*              .filter((tramId) => tramId !== 'detector')*/}
      {/*              .map((tramId) => {*/}
      {/*                const value = row[tramId];*/}
      {/*                const getHeatColor = (value: number) => {*/}
      {/*                  if (value === 0) return '#ffffff';*/}
      {/*                  if (value === 1) return '#ffe082';*/}
      {/*                  if (value === 2) return '#ffb74d';*/}
      {/*                  if (value === 3) return '#ff8a65';*/}
      {/*                  if (value >= 4) return '#e53935';*/}
      {/*                  return '#ffffff';*/}
      {/*                };*/}
      {/*                const color = getHeatColor(value);*/}
      {/*                return (*/}
      {/*                  <TableCell*/}
      {/*                    key={tramId}*/}
      {/*                    align="center"*/}
      {/*                    sx={{*/}
      {/*                      backgroundColor: color,*/}
      {/*                      width: 30,*/}
      {/*                      height: 30,*/}
      {/*                      padding: 0,*/}
      {/*                      border: '1px solid #ddd',*/}
      {/*                      color: value > 3 ? '#fff' : '#000',*/}
      {/*                    }}*/}
      {/*                  >*/}
      {/*                    {value > 0 ? value : ''}*/}
      {/*                  </TableCell>*/}
      {/*                );*/}
      {/*              })}*/}
      {/*          </TableRow>*/}
      {/*        ))}*/}
      {/*      </TableBody>*/}
      {/*    </Table>*/}
      {/*  </Box>*/}
      {/*</Box>*/}
    </Box>
  );
};

export default WarningDashboard;
